import { EditorPopup } from '@tunasong/editor'
import { Chat as ChatComponent } from '@tunasong/plugin-chat/ui'
import { useEditorMessage } from '@tunasong/plugin-lib'
import type { EditorEvent, TunaPluginRenderProps } from '@tunasong/plugin-lib'
import { isCoreElement } from '@tunasong/schemas'
import type { Chat, CoreElement, Persisted } from '@tunasong/schemas'
import { Button, CircularProgress, FocusLock, getCursorStaticAnchorEl } from '@tunasong/ui-lib'
import { useCallback, useEffect, useState } from 'react'
import type { FC } from 'react'

import { commentAnchorFor } from './commented-blocks.js'
import { useCommentsChat } from './comments.hook.js'
import { usePublishCommentedBlocks } from './use-publish-commented-blocks.js'
import styles from './comments.module.css'

export interface CommentsProps extends TunaPluginRenderProps {}

const Comments: FC<CommentsProps> = props => {
  const { editor } = props
  const [element, setElement] = useState<CoreElement | null>()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const [open, setOpen] = useState(false)
  const [currentChat, setCurrentChat] = useState<Persisted<Chat>>()
  /** Creating the thread can fail; without this the popup spun forever (2026-10-19 phone audit) */
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  const { chat, chatLoading, create: createChat } = useCommentsChat(editor, element)
  /** Chat makeover M3 (#2718): the sidebar Chat groups this song's block threads and opens them from here */
  usePublishCommentedBlocks(editor)

  const handleShowComments = (ev: EditorEvent) => {
    const { nodeEntry } = ev.detail
    const el = nodeEntry[0]
    setAnchorEl(getCursorStaticAnchorEl() as HTMLElement | null)
    if (isCoreElement(el)) {
      setElement(el)
      setOpen(true)
    }
  }

  /** We use currentChat so we can set it in the effect below */
  useEffect(() => {
    setCurrentChat(chat)
  }, [chat])

  /**
   * When the element is set we also open the window. We do this in an effect
   * because we need useCommentsChat to evaluate with the element.
   */
  useEffect(() => {
    if (!element || chatLoading || chat || failed) {
      return
    }
    /** Chat is not loading, and is not set. We need to create it. `attempt` re-runs this for Try again. */
    void createChat()
      ?.then(c => {
        setCurrentChat(c)
      })
      .catch(() => setFailed(true))
  }, [chat, chatLoading, createChat, element, failed, attempt])

  const handleRetry = () => {
    setFailed(false)
    setAttempt(n => n + 1)
  }

  const handleHideComments = useCallback(() => {
    setOpen(false)
    setElement(null)
    setAnchorEl(null)
    setFailed(false)
  }, [])

  /** We want to listen once, on the the top-level */
  useEditorMessage({ editor, type: 'show-comments', handler: handleShowComments })

  if (!(element && open)) {
    return null
  }

  /** GH #2795: each comment written here carries where it was written, so its notification can name the block */
  const anchor = commentAnchorFor({ nodes: editor.children, entityId: editor.rootElement?.id, elementId: element.id })

  return (
    <EditorPopup
      editor={editor}
      focusLock={true}
      open={open}
      anchorEl={anchorEl}
      onClose={handleHideComments}
      className={styles.popup}
    >
      {failed ? (
        <div role="alert" className={styles.failed}>
          <p>Couldn&apos;t open comments.</p>
          <Button onClick={handleRetry}>Try again</Button>
        </div>
      ) : !currentChat ? (
        <CircularProgress className={styles.spinner} aria-label="Loading comments" />
      ) : null}
      {currentChat && (
        <FocusLock returnFocus={true}>
          <ChatComponent element={currentChat} commentMetadata={anchor ? { anchor } : undefined} />
        </FocusLock>
      )}
    </EditorPopup>
  )
}

export default Comments
